import { observer } from "mobx-react-lite";
import { FC, useState, useCallback } from "react";
import styled from "@emotion/styled";
import { MemLogoWithShadow } from "@/components/static/MemLogoWithShadow";
import { DebugModeTapDetector } from "@/components/layout/components/account-profile/DebugModeTapDetector";
import { AccountInfo } from "@/components/layout/components/account-info/AccountInfo";
import { OAuthCardWrapper } from "@/pages/oauth-consent/oauth-card-wrapper";
import { MdsCardKind } from "@/design-system/components/card";
import { MDSButton, MDSButtonVariant } from "@/mds";

enum CardState {
  Initial = "initial",
  Approved = "approved",
  Denied = "denied",
  Error = "error",
}

export interface OAuthCardProps {
  appName: string;
  /**
   * Not currently used.
   * In the future, we'll show the "real" scopes from the backend.
   */
  scopeStr: string;
  accountInfo: {
    profilePhotoUrl: string | null;
    profileDisplayName: string;
    profileEmailAddress: string;
  };
  handleApprove: () => Promise<void>;
  handleDeny: () => Promise<void>;
}

export const OAuthCard: FC<OAuthCardProps> = observer(
  ({ appName, accountInfo, handleApprove, handleDeny }) => {
    const [isApproveLoading, setIsApproveLoading] = useState(false);
    const [isDenyLoading, setIsDenyLoading] = useState(false);
    const [cardState, setCardState] = useState<CardState>(CardState.Initial);

    const onClickApprove = useCallback(async () => {
      setIsApproveLoading(true);
      try {
        await handleApprove();
        setCardState(CardState.Approved);
      } catch (_unknownErr) {
        setCardState(CardState.Error);
      } finally {
        setIsApproveLoading(false);
      }
    }, [handleApprove]);

    const onClickDeny = useCallback(async () => {
      setIsDenyLoading(true);
      try {
        await handleDeny();
        setCardState(CardState.Denied);
      } catch (_unknownErr) {
        setCardState(CardState.Error);
      } finally {
        setIsDenyLoading(false);
      }
    }, [handleDeny]);

    if (cardState === CardState.Approved) {
      return (
        <OAuthCardWrapper kind={MdsCardKind.LargeShadow}>
          <CardSection>
            <TitleSection>Access Approved</TitleSection>
            <MessageSection>
              You have successfully granted access to {appName}. You may now close this window.
            </MessageSection>
          </CardSection>
        </OAuthCardWrapper>
      );
    }

    if (cardState === CardState.Denied) {
      return (
        <OAuthCardWrapper kind={MdsCardKind.LargeShadow}>
          <CardSection>
            <TitleSection>Canceled</TitleSection>
            <MessageSection>You may now close this window.</MessageSection>
          </CardSection>
        </OAuthCardWrapper>
      );
    }

    if (cardState === CardState.Error) {
      return (
        <OAuthCardWrapper kind={MdsCardKind.LargeShadow}>
          <CardSection>
            <TitleSection>Something went wrong</TitleSection>
            <MessageSection>
              An error occurred while processing your request. Please refresh the page and try
              again.
            </MessageSection>
          </CardSection>
        </OAuthCardWrapper>
      );
    }

    return (
      <OAuthCardWrapper kind={MdsCardKind.LargeShadow}>
        <CardSection>
          <TitleSection>{appName} wants permission to access your Mem Account</TitleSection>

          <DebugModeTapDetector>
            <MemLogoWithShadow />
          </DebugModeTapDetector>

          <AccountSection>
            <AccountInfo
              profilePhotoUrl={accountInfo.profilePhotoUrl}
              profileDisplayName={accountInfo.profileDisplayName}
              profileEmailAddress={accountInfo.profileEmailAddress}
            />
          </AccountSection>

          <ScopeSection>
            <ScopeMessage>This will allow {appName} to:</ScopeMessage>
            <ScopeItem>• Access your notes</ScopeItem>
            <ScopeItem>• Access your collections</ScopeItem>
          </ScopeSection>

          <PolicySection>
            By clicking Allow Access, you allow this app to use your information in accordance to
            their terms of service and privacy policies. You can change this at any time in the Mem
            application.
          </PolicySection>
        </CardSection>

        <ActionSection>
          <MDSButton
            variant={MDSButtonVariant.Transparent}
            onClick={onClickDeny}
            isDisabled={isApproveLoading}
            isLoading={isDenyLoading}
          >
            Cancel
          </MDSButton>
          <MDSButton
            variant={MDSButtonVariant.Contrast}
            onClick={onClickApprove}
            isDisabled={isDenyLoading}
            isLoading={isApproveLoading}
          >
            Allow Access
          </MDSButton>
        </ActionSection>
      </OAuthCardWrapper>
    );
  }
);

const AccountSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 8px;
`;

const TitleSection = styled.div`
  color: ${({ theme }) => theme.colors.grey.x600};
  font-size: ${({ theme }) => theme.fontSizes.xlarge};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  letter-spacing: ${({ theme }) => theme.lineSpacings.large};
  margin-bottom: 28px;
  text-align: center;
`;

const ScopeSection = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: flex-start;
  width: 100%;
  gap: 8px;
  margin-top: 32px;
  margin-bottom: 32px;
`;

const ScopeMessage = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.grey.x800};
`;

const ScopeItem = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.grey.x800};
`;

const PolicySection = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xxxsmall};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.grey.x500};
`;

const MessageSection = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.grey.x600};
  text-align: center;
  margin-top: 16px;
`;

const CardSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const ActionSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-top: 24px;
  justify-content: space-between;
  width: 100%;
`;
